'use client';

import {
  Box,
  Button,
  Checkbox,
  Flex,
  freddyTheme,
  Link,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalFooter,
  Tag,
  Typography
} from '@coolblue-development/freddy';
import confirmProductPlusConsentTrigger from '@coolblue-development/ga4-events/triggers/ProductPlus/confirmProductPlusConsentTrigger';
import deselectProductPlusTrigger from '@coolblue-development/ga4-events/triggers/ProductPlus/deselectProductPlusTrigger';
import readTermsAndConditionsTrigger from '@coolblue-development/ga4-events/triggers/ProductPlus/readTermsAndConditionsTrigger';
import selectTermsAndConditionsTrigger from '@coolblue-development/ga4-events/triggers/ProductPlus/selectTermsAndConditionsTrigger';
import { ArrowRight } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import { ProductTitle, SquareProductImage } from '@coolblue-development/next-patterns/client';
import FreeWashingIcon from '@features/product/components/OrderBlock/FreeWashingProposition/FreeWashingIcon';
import { useTranslation } from '@i18n/useTranslation';
import { useState } from 'react';

type Props = {
  checked: boolean;
  freeWashingUrl: string;
  onChange: (value: boolean) => void;
  open: boolean;
  onClose: () => void;
  productImageId: string;
  productId: number;
};

export default function FreeWashingConsentModal({
  checked,
  freeWashingUrl,
  onChange,
  open,
  onClose,
  productImageId,
  productId
}: Props) {
  const modalTranslations = useFreddyTranslations('modal');
  const { t, Trans } = useTranslation('webshop.productPage.freeWashing.modal');
  const [isChecked, setIsChecked] = useState(checked);
  const [isError, setIsError] = useState(false);
  const { trackEvent } = useAnalytics();
  const redirectTitle: string = t('readMore');

  const readTermsAndConditionsTriggerParams = {
    sub_feature: 'free_washing' as const,
    index: 1,
    total_indices: 1,
    url: freeWashingUrl,
    redirect: redirectTitle
  };

  const deselectProductPlusTriggerParams = {
    sub_feature: 'free_washing' as const,
    index: 1,
    total_indices: 1,
    item_id: productId,
    list: ''
  };

  const confirmProductPlusConsentTriggerParams = {
    sub_feature: 'free_washing' as const,
    status: 'success' as const
  };

  const onSubmit = () => {
    if (!isChecked) {
      setIsError(true);

      return;
    }

    onChange(isChecked);
    onClose();
    trackEvent(confirmProductPlusConsentTrigger(confirmProductPlusConsentTriggerParams));
  };

  return (
    <Modal
      body={
        <ModalBody>
          <Flex gap={400} alignItems='center'>
            <Flex alignItems='center'>
              <SquareProductImage size='large' alt={t('productTitle')} imageId={productImageId} />
              <Box sx={{ marginLeft: '-35px', zIndex: 1 }}>
                <FreeWashingIcon />
              </Box>
            </Flex>
            <Box>
              <Tag mb={100} variant='subtle'>
                {t('productLabel')}
              </Tag>
              <ProductTitle>
                <ProductTitle.Title>{t('productTitle')}</ProductTitle.Title>
              </ProductTitle>
            </Box>
          </Flex>
          <Typography mt={600}>{t('description')}</Typography>
          <Typography variant='h3' mt={600}>
            {t('instructionTitle')}
          </Typography>
          <List component='ol' marker='emphasised' mt={400}>
            <ListItem>
              <Typography component='span'>
                <Trans
                  t={t}
                  i18nKey='instructionSteps.one'
                  components={{
                    strong: <strong></strong>
                  }}
                />
              </Typography>
            </ListItem>
            <ListItem>
              <Typography component='span'>
                <Trans
                  t={t}
                  i18nKey='instructionSteps.two'
                  components={{
                    strong: <strong></strong>
                  }}
                />
              </Typography>
            </ListItem>
            <ListItem>
              <Typography component='span'>
                <Trans
                  t={t}
                  i18nKey='instructionSteps.three'
                  components={{
                    strong: <strong></strong>
                  }}
                />
              </Typography>
            </ListItem>
            <ListItem>
              <Typography component='span'>
                <Trans
                  t={t}
                  i18nKey='instructionSteps.four'
                  components={{
                    strong: <strong></strong>
                  }}
                />
              </Typography>
            </ListItem>
          </List>
          <Link
            href={freeWashingUrl}
            mt={300}
            target='_blank'
            startIcon={<ArrowRight />}
            onClick={() =>
              trackEvent(readTermsAndConditionsTrigger(readTermsAndConditionsTriggerParams))
            }
          >
            {redirectTitle}
          </Link>
          <Box
            sx={{
              backgroundColor: freddyTheme.color.background.secondary,
              borderRadius: freddyTheme.borderRadius[100],
              marginTop: freddyTheme.spacing[400],
              padding: freddyTheme.spacing[400]
            }}
          >
            <Typography variant='h4' mb={300}>
              {t('consentTitle')}
            </Typography>
            <Checkbox
              label={t('consentLabel')}
              checked={isChecked}
              onChange={event => {
                setIsChecked(event.target.checked);
                if (event.target.checked) {
                  trackEvent(
                    selectTermsAndConditionsTrigger({
                      sub_feature: 'free_washing' as const
                    })
                  );
                }
                if (!event.target.checked) {
                  trackEvent(deselectProductPlusTrigger(deselectProductPlusTriggerParams));
                }
                setIsError(false);
              }}
            />
            {isError && (
              <Typography mt={200} color='sentimentDanger'>
                {t('consentError')}
              </Typography>
            )}
          </Box>
        </ModalBody>
      }
      footer={
        <ModalFooter>
          <Button variant='secondary' onClick={onClose} fullWidth>
            {t('cancel')}
          </Button>
          <Button onClick={onSubmit} fullWidth>
            {t('submit')}
          </Button>
        </ModalFooter>
      }
      onClose={onClose}
      open={open}
      placement={{ xs: 'bottom', md: 'center' }}
      size='large'
      title={t('title')}
      translations={modalTranslations}
    />
  );
}
